@if (isOpen$ | async) {
  <!-- Confetti Canvas (removed after animation) -->
  @if (showConfetti$ | async) {
    <div class="confetti-wrapper">
      <app-confetti-canvas
        [triggerBurstOnInit]="false"
        [particleCount]="150"
        [colors]="['#f56600', '#fb923c', '#fcd34d', '#a855f7', '#ec4899', '#22c55e']"
      ></app-confetti-canvas>
    </div>
  }

  <!-- Modal Backdrop -->
  <div
    class="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in"
  >
    <!-- Modal Content -->
    <div
      class="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-modal-pop"
      (click)="$event.stopPropagation()"
    >
      <!-- Decorative Background -->
      <div class="absolute inset-0 overflow-hidden pointer-events-none">
        <div class="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-orange-400/20 to-pink-400/20 rounded-full blur-3xl"></div>
        <div class="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-tr from-purple-400/20 to-blue-400/20 rounded-full blur-3xl"></div>
      </div>

      @if (isVerifying$ | async) {
        <!-- Loading/Verifying State -->
        <div class="relative p-8 text-center">
          <!-- Animated Loading Badge -->
          <div class="relative inline-flex mb-6">
            <div class="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-pink-500 flex items-center justify-center shadow-lg shadow-orange-500/30 animate-pulse">
              <lucide-icon [img]="icons.Loader2" class="w-12 h-12 text-white animate-spin"></lucide-icon>
            </div>
          </div>

          <!-- Loading Title -->
          <h2 class="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 mb-2">
            Activating Your Subscription...
          </h2>

          <p class="text-gray-500 mb-6">
            Please wait while we set up your premium account
          </p>

          <!-- Progress indicator -->
          <div class="w-full bg-gray-200 rounded-full h-2 mb-4 overflow-hidden">
            <div 
              class="bg-gradient-to-r from-brand via-orange-500 to-pink-500 h-2 rounded-full transition-all duration-100 ease-out"
              [style.width.%]="progress$ | async"
            ></div>
          </div>

          <p class="text-xs text-gray-400">
            This usually takes just a few seconds...
          </p>
        </div>
      } @else {
        <!-- Close Button (only visible when not verifying) -->
        <button
          (click)="close()"
          class="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors cursor-pointer"
        >
          <lucide-icon [img]="icons.X" class="w-4 h-4 text-gray-500"></lucide-icon>
        </button>

        <!-- Success Content -->
        <div class="relative p-8 text-center">
          <!-- Animated Crown Badge -->
          <div class="relative inline-flex mb-6">
            <div class="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-pink-500 flex items-center justify-center animate-glow shadow-lg shadow-orange-500/30">
              <lucide-icon [img]="icons.Crown" class="w-12 h-12 text-white drop-shadow-lg"></lucide-icon>
            </div>
            <!-- Floating Stars -->
            <div class="absolute -top-2 -right-2 animate-float-slow">
              <lucide-icon [img]="icons.Sparkles" class="w-6 h-6 text-amber-400"></lucide-icon>
            </div>
            <div class="absolute -bottom-1 -left-3 animate-float-fast">
              <lucide-icon [img]="icons.Star" class="w-5 h-5 text-pink-400 fill-current"></lucide-icon>
            </div>
          </div>

          <!-- Celebration Title -->
          <h2 class="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-600 via-pink-600 to-purple-600 mb-2 animate-shimmer">
            Congratulations!
          </h2>

          <p class="text-gray-500 mb-8">
            You've unlocked all the premium features! 🎉
          </p>

          <!-- Features List -->
          <div class="bg-gradient-to-br from-gray-50 to-orange-50/50 rounded-2xl p-5 mb-8 text-left">
            <h3 class="font-bold text-gray-800 mb-4 flex items-center gap-2 text-sm uppercase tracking-wide">
              <lucide-icon [img]="icons.Rocket" class="w-4 h-4 text-brand"></lucide-icon>
              Your Premium Benefits
            </h3>

            <ul class="space-y-3">
              @for (feature of features; track feature) {
                <li class="flex items-center gap-3 text-gray-700">
                  <div class="w-5 h-5 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shrink-0 shadow-sm">
                    <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>
                  <span class="text-sm">{{ feature }}</span>
                </li>
              }
            </ul>
          </div>

          <!-- CTA Button -->
          <button
            (click)="close()"
            class="w-full py-4 px-6 bg-gradient-to-r from-brand via-orange-500 to-pink-500 hover:from-brand-dark hover:via-orange-600 hover:to-pink-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30 transform hover:scale-[1.02] cursor-pointer flex items-center justify-center gap-2"
          >
            <lucide-icon [img]="icons.PartyPopper" class="w-5 h-5"></lucide-icon>
            Start Creating Amazing Lessons
          </button>

          <p class="mt-4 text-xs text-gray-400">
            Your subscription is now active
          </p>
        </div>
      }
    </div>
  </div>
}

